import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IConferenceDeprecatedService } from '@agent/core/phone/conference/conference-deprecated.service';
import { GuestState, IGuest } from '@agent/core/phone/conference/models/guest';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { ILogService, IPromise, IQService, isDefined } from 'angular';

import {
  COMETD_SCRIPT_ANCHOR_REDIRECTION_DONE,
  CSR_FACADE_PREFIX,
  EntryPointType,
  MessagingService,
  Option,
  StorageService,
} from '@contact/upgrade';

import { ScriptAnchor } from './models/script-anchor';
import { IScriptRedirect, ScriptRedirectionIsDone } from './models/script-redirection';

const MODULE_NAME = 'scriptRedirection';
const SCRIPT_REDIRECT_MAP_KEY = 'scriptRedirectMap';

export interface IScriptRedirectionService {
  getScriptAnchors(responseId: string): IPromise<ScriptAnchor[]>;

  executeScriptRedirection(responseId: string): IPromise<any>;

  createScriptRedirect(responseId: string): IScriptRedirect;

  getScriptRedirect(responseId: string): Option<IScriptRedirect>;

  updateScriptRedirect(responseId: string, scriptRedirect: IScriptRedirect): void;

  removeScriptRedirect(responseId: string): void;

  canValidateScriptRedirect(responseId: string): boolean;

  isRedirectInProgress(responseId: string): boolean;

  unparkContactAndAgent(responseId: string): IPromise<any>;
}

export class ScriptRedirectionService implements IScriptRedirectionService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    'httpDeprecated',
    'sessionDeprecatedService',
    'messaging',
    'storage',
    'conferenceDeprecatedService',
    'contactDeprecatedService',
  ];

  public name = MODULE_NAME;

  private availableScriptAnchor: { [responseId: string]: ScriptAnchor[] } = {};
  private scriptRedirectMap: { [responseId: string]: IScriptRedirect } = {};

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly messaging: MessagingService,
    private readonly storage: StorageService,
    private readonly conferenceDeprecatedService: IConferenceDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService
  ) {
    this.scriptRedirectMap = this.storage.get(SCRIPT_REDIRECT_MAP_KEY).getOrElse({});

    this.messaging
      .forRemote()
      .listen$(COMETD_SCRIPT_ANCHOR_REDIRECTION_DONE)
      .subscribe(message => this.handleScriptRedirectionIsDone(message));
  }

  createScriptRedirect(responseId: string): IScriptRedirect {
    let scriptRedirect = this.getScriptRedirect(responseId);
    if (scriptRedirect.isDefined()) {
      this.$log.warn(`[scriptRedirect] already existing tranfer with response id[${responseId}]. Returning it.`);
      return scriptRedirect.get();
    }

    this.scriptRedirectMap[responseId] = {
      type: EntryPointType.RELEASE_CSR,
      inProgress: false,
      done: false,
      csrConnectionId: '',
      waitForHttpResponse: false,
    };
    if (
      !(
        this.sessionDeprecatedService.hasPermission('CSR_PHONE_INCOMING::contact:redirect_to_script_anchor_with_csr_release') ||
        this.sessionDeprecatedService.hasPermission('CSR_PHONE_OUTGOING::contact:redirect_to_script_anchor_with_csr_release')
      )
    ) {
      this.scriptRedirectMap[responseId].type = EntryPointType.PARK_CSR;
    }

    this.storage.store(SCRIPT_REDIRECT_MAP_KEY, this.scriptRedirectMap);
    this.$log.info(`[scriptRedirect] add script redirect configuration for response with id[${responseId}].`);

    return this.scriptRedirectMap[responseId];
  }

  getScriptRedirect(responseId: string): Option<IScriptRedirect> {
    return Option.of(this.scriptRedirectMap[responseId]).orElseFunc(() =>
      // if scritpRedirect map is empty, try to loads saved one
      this.storage.get(SCRIPT_REDIRECT_MAP_KEY).flatMap(scriptRedirectMap => Option.of(scriptRedirectMap[responseId]))
    );
  }

  updateScriptRedirect(responseId: string, scriptRedirect: IScriptRedirect): void {
    if (this.scriptRedirectMap.hasOwnProperty(responseId)) {
      this.scriptRedirectMap[responseId] = scriptRedirect;
      this.storage.store(SCRIPT_REDIRECT_MAP_KEY, this.scriptRedirectMap);
    }
  }

  removeScriptRedirect(responseId: string) {
    if (this.scriptRedirectMap.hasOwnProperty(responseId)) {
      delete this.scriptRedirectMap[responseId];
      this.storage.store(SCRIPT_REDIRECT_MAP_KEY, this.scriptRedirectMap);
      this.$log.info(`[scriptRedirect] removed script redirect configuration for response with id[${responseId}].`);
    }
  }

  getScriptAnchors(responseId: string): IPromise<ScriptAnchor[]> {
    let deferred = this.$q.defer<ScriptAnchor[]>();
    if (this.availableScriptAnchor.hasOwnProperty(responseId)) {
      deferred.resolve(this.availableScriptAnchor[responseId]);
    } else {
      this.httpDeprecated
        .asPromise()
        .post(CSR_FACADE_PREFIX + '/getScriptAnchors', [responseId])
        .then(
          scriptAnchorList => {
            this.availableScriptAnchor[responseId] = [];
            scriptAnchorList.data.anchors.map(anchor => {
              this.availableScriptAnchor[responseId].push(
                new ScriptAnchor(anchor.uuid, anchor.name, anchor.alias, EntryPointType[anchor.type as string])
              );
            });
            deferred.resolve(this.availableScriptAnchor[responseId]);
          },
          () => {
            deferred.resolve([]);
          }
        );
    }
    return deferred.promise;
  }

  executeScriptRedirection(responseId: string): IPromise<any> {
    let deferred = this.$q.defer();

    let scriptRedirect: IScriptRedirect = this.getScriptRedirect(responseId).get();
    if (scriptRedirect.waitForHttpResponse) {
      deferred.reject(`[scriptRedirect]script redirect already in progress for response[${responseId}]!`);
      this.$log.warn(`[scriptRedirect]script redirect already in progress for response[${responseId}]!`);
      return deferred.promise;
    }
    scriptRedirect.waitForHttpResponse = true;
    this.$log.info(`[scriptRedirect] execute script redirect for response with id[${responseId}].`);
    let contact: Option<IGuest> = this.conferenceDeprecatedService.getContactGuest(responseId);

    let action = () => {
      if (isDefined(scriptRedirect) && scriptRedirect.target) {
        this.httpDeprecated
          .asPromise()
          .withConfig({
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
            },
          })
          .post(CSR_FACADE_PREFIX + '/redirectToScriptAnchor', [responseId, scriptRedirect.target.id, EntryPointType[scriptRedirect.type]])
          .then(
            requestAck => {
              scriptRedirect.waitForHttpResponse = false;
              if (!isDefined(requestAck) || !requestAck.data) {
                deferred.reject('Script redirection execution request failed');
                this.$log.error(`[scriptRedirect] error when execute script redirect for response with id[${responseId}].`);
              } else {
                scriptRedirect.inProgress = true;
                this.storage.store(SCRIPT_REDIRECT_MAP_KEY, this.scriptRedirectMap);
                if (scriptRedirect.target && scriptRedirect.target.type === EntryPointType.PARK_CSR) {
                  this.conferenceDeprecatedService.parkAgent(responseId);
                  this.conferenceDeprecatedService.setContactParked(responseId);
                }
                this.$log.info(`[scriptRedirect] execute script redirect for response with id[${responseId}] done.`);
                deferred.resolve();
              }
            },
            () => {
              scriptRedirect.waitForHttpResponse = false;
            }
          );
      } else {
        deferred.reject('No script redirection found for responseId[' + responseId + ']');
      }
    };

    if (contact.isDefined() && contact.get().state === GuestState.PARKED) {
      this.conferenceDeprecatedService.unparkContact(responseId).then(
        () => {
          // TODO remove this uggly fix
          setTimeout(() => action(), 200);
        },
        () => {
          action();
        }
      );
    } else {
      action();
    }

    return deferred.promise;
  }

  unparkContactAndAgent(responseId: string): IPromise<any> {
    let deferred = this.$q.defer();

    this.$log.info(`[scriptRedirect] unpark csr and contact for response with id[${responseId}].`);

    let scriptRedirect: IScriptRedirect = this.getScriptRedirect(responseId).get();

    if (isDefined(scriptRedirect)) {
      this.conferenceDeprecatedService.unparkContactAndAgent(responseId).then(
        () => deferred.resolve(),
        () => deferred.reject()
      );
    } else {
      deferred.reject();
    }

    return deferred.promise;
  }

  canValidateScriptRedirect(responseId: string): boolean {
    return !this.isRedirectInProgress(responseId) && this.getScriptRedirect(responseId).get().target != null;
  }

  isRedirectInProgress(responseId: string): boolean {
    return this.getScriptRedirect(responseId).get().inProgress;
  }

  private handleScriptRedirectionIsDone(message) {
    let scriptAnchorRedirectionIsDone: ScriptRedirectionIsDone = message;

    if (
      isDefined(scriptAnchorRedirectionIsDone) &&
      isDefined(scriptAnchorRedirectionIsDone.bookingUuid) &&
      this.scriptRedirectMap.hasOwnProperty(scriptAnchorRedirectionIsDone.bookingUuid)
    ) {
      // save csr connection id
      this.scriptRedirectMap[scriptAnchorRedirectionIsDone.bookingUuid].csrConnectionId = scriptAnchorRedirectionIsDone.csrConnectionId;
      this.scriptRedirectMap[scriptAnchorRedirectionIsDone.bookingUuid].done = true;
      this.contactDeprecatedService.updateContext(
        scriptAnchorRedirectionIsDone.bookingUuid,
        scriptAnchorRedirectionIsDone.extendedContactInfoMap
      );
    }
    // update contact context
    this.contactDeprecatedService.updateContext(
      scriptAnchorRedirectionIsDone.bookingUuid,
      scriptAnchorRedirectionIsDone.extendedContactInfoMap
    );
  }
}

export default MODULE_NAME;
